/**
 * /crm/correo · Bandeja de correo del CRM (Fase 1).
 *
 * Layout 3 paneles tipo Linear/Notion Mail:
 * ┌────────────┬──────────────────┬──────────────────────────┐
 * │ Bandejas   │ Listado mensajes │  Lector + Composer       │
 * └────────────┴──────────────────┴──────────────────────────┘
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Mail, Inbox, Send, Archive, Star, Search, RefreshCw, Settings,
  ChevronRight, Paperclip, Reply, AlertCircle, Link2, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import API from '@/lib/api';

const BANDEJAS = [
  { key: 'INBOX', label: 'Entrada', icon: Inbox, filter: { folder: 'INBOX', archived: false } },
  { key: 'UNREAD', label: 'No leídos', icon: Mail, filter: { unread: true, archived: false } },
  { key: 'SENT', label: 'Enviados', icon: Send, filter: { direction: 'outbound' } },
  { key: 'IMPORTANT', label: 'Importantes', icon: Star, filter: { important: true, archived: false } },
  { key: 'ARCHIVED', label: 'Archivados', icon: Archive, filter: { archived: true } },
];

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

export default function Correo() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [bandeja, setBandeja] = useState('INBOX');
  const [items, setItems] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(false);
  const [contadores, setContadores] = useState({ no_leidos: 0, total: 0 });

  // Cargar cuentas + contadores al inicio
  useEffect(() => {
    API.get('/email/accounts').then(r => setAccounts(r.data?.accounts || [])).catch(() => {});
    API.get('/email/counters').then(r => setContadores(r.data || {})).catch(() => {});
  }, []);

  // Cargar mensajes según bandeja
  const cargarMensajes = useCallback(async () => {
    setLoading(true);
    try {
      const filter = BANDEJAS.find(b => b.key === bandeja)?.filter || {};
      const params = { ...filter, limit: 100 };
      if (busqueda) params.search = busqueda;
      const { data } = await API.get('/email/messages', { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error('Error cargando correos');
    } finally {
      setLoading(false);
    }
  }, [bandeja, busqueda]);

  useEffect(() => { cargarMensajes(); }, [cargarMensajes]);

  const handleSync = async () => {
    if (accounts.length === 0) {
      toast.error('No hay cuentas configuradas');
      return;
    }
    setSyncing(true);
    try {
      let totalNuevos = 0;
      for (const a of accounts) {
        const { data } = await API.post(`/email/accounts/${a.id}/sync`);
        totalNuevos += data.nuevos || 0;
      }
      toast.success(`Sincronización completada · ${totalNuevos} nuevo(s)`);
      cargarMensajes();
      API.get('/email/counters').then(r => setContadores(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const handleOpen = async (m) => {
    try {
      const { data } = await API.get(`/email/messages/${m.id}`);
      setSelected(data);
      if (!m.is_read) {
        API.post(`/email/messages/${m.id}/read`).catch(() => {});
        setItems(prev => prev.map(x => x.id === m.id ? { ...x, is_read: true } : x));
      }
    } catch (e) {
      toast.error('No se pudo abrir el mensaje');
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex" data-testid="correo-page">
      {/* Panel 1 · Bandejas */}
      <aside className="w-56 border-r p-3 space-y-1 overflow-y-auto">
        <Button
          className="w-full mb-3"
          onClick={() => setComposing(true)}
          disabled={accounts.length === 0}
          data-testid="redactar-btn"
        >
          <Mail className="w-4 h-4 mr-2" /> Redactar
        </Button>
        {accounts.length === 0 && (
          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
            No hay cuenta configurada. <Link to="/crm/configuracion/email" className="underline">Configurar</Link>
          </div>
        )}
        {BANDEJAS.map(b => (
          <button
            key={b.key}
            onClick={() => { setBandeja(b.key); setSelected(null); }}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              bandeja === b.key ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
            }`}
            data-testid={`bandeja-${b.key}`}
          >
            <span className="flex items-center gap-2">
              <b.icon className="w-4 h-4" />
              {b.label}
            </span>
            {b.key === 'UNREAD' && contadores.no_leidos > 0 && (
              <Badge variant="default" className="text-xs h-5">{contadores.no_leidos}</Badge>
            )}
          </button>
        ))}
        <div className="pt-3 border-t mt-3 space-y-1">
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => navigate('/crm/configuracion/email')}>
            <Settings className="w-4 h-4 mr-2" /> Configuración
          </Button>
        </div>
      </aside>

      {/* Panel 2 · Listado */}
      <section className="w-96 border-r flex flex-col">
        <div className="p-3 border-b space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar correos…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-9"
              data-testid="buscar-correos"
            />
          </div>
          <Button variant="outline" size="sm" className="w-full" onClick={handleSync} disabled={syncing} data-testid="sync-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Sin mensajes</div>
          ) : (
            items.map(m => (
              <button
                key={m.id}
                onClick={() => handleOpen(m)}
                className={`w-full text-left border-b px-3 py-3 hover:bg-muted/40 transition-colors ${
                  selected?.id === m.id ? 'bg-muted/60' : ''
                } ${!m.is_read ? 'font-medium' : ''}`}
                data-testid={`msg-${m.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-0.5">
                  <span className="text-sm truncate flex-1">
                    {!m.is_read && <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2 align-middle" />}
                    {m.from?.replace(/<.*>/, '').trim() || m.to?.join(', ')}
                  </span>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">{formatDate(m.date)}</span>
                </div>
                <p className={`text-sm truncate ${!m.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {m.subject || '(sin asunto)'}
                </p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {m.orden_id && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-emerald-500/40 text-emerald-700">
                      <Link2 className="w-2.5 h-2.5 mr-0.5" />
                      OT
                    </Badge>
                  )}
                  {m.attachments?.length > 0 && (
                    <Paperclip className="w-3 h-3 text-muted-foreground" />
                  )}
                  {m.important && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                </div>
              </button>
            ))
          )}
        </div>
      </section>

      {/* Panel 3 · Lector */}
      <section className="flex-1 overflow-y-auto">
        {composing ? (
          <Composer
            accounts={accounts}
            onClose={() => setComposing(false)}
            onSent={() => { setComposing(false); cargarMensajes(); }}
          />
        ) : selected ? (
          <MessageViewer message={selected} onReply={() => setComposing(true)} />
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Selecciona un correo</p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function MessageViewer({ message, onReply }) {
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-xl font-semibold">{message.subject || '(sin asunto)'}</h2>
        <Button variant="outline" size="sm" onClick={onReply} data-testid="reply-btn">
          <Reply className="w-3.5 h-3.5 mr-1.5" /> Responder
        </Button>
      </div>
      <div className="bg-muted/30 rounded-lg p-3 text-sm space-y-1">
        <p><strong>De:</strong> {message.from}</p>
        <p><strong>Para:</strong> {(message.to || []).join(', ')}</p>
        {message.cc?.length > 0 && <p><strong>CC:</strong> {message.cc.join(', ')}</p>}
        <p><strong>Fecha:</strong> {new Date(message.date).toLocaleString('es-ES')}</p>
        {message.orden_id && (
          <p>
            <strong>OT vinculada:</strong>{' '}
            <Link to={`/crm/ordenes/${message.orden_id}`} className="text-primary underline">
              Ver orden
            </Link>
          </p>
        )}
      </div>
      {message.attachments?.length > 0 && (
        <div className="text-sm">
          <p className="font-medium mb-2">Adjuntos:</p>
          <div className="flex flex-wrap gap-2">
            {message.attachments.map((a, i) => (
              <Badge key={i} variant="outline" className="gap-1">
                <Paperclip className="w-3 h-3" />
                {a.filename} ({Math.round((a.size || 0) / 1024)} KB)
              </Badge>
            ))}
          </div>
        </div>
      )}
      <div className="prose prose-sm max-w-none border-t pt-4">
        {message.body_html ? (
          <div dangerouslySetInnerHTML={{ __html: message.body_html }} />
        ) : (
          <pre className="whitespace-pre-wrap font-sans text-sm">{message.body_text || '(sin contenido)'}</pre>
        )}
      </div>
    </div>
  );
}

function Composer({ accounts, onClose, onSent, replyTo = null, ordenId = null }) {
  const [accountId, setAccountId] = useState(accounts[0]?.id || '');
  const [to, setTo] = useState(replyTo?.from ? [replyTo.from.match(/<([^>]+)>/)?.[1] || replyTo.from] : []);
  const [toInput, setToInput] = useState(to.join(', '));
  const [subject, setSubject] = useState(replyTo ? `Re: ${replyTo.subject || ''}` : '');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    const toArr = toInput.split(',').map(s => s.trim()).filter(Boolean);
    if (toArr.length === 0) { toast.error('Indica al menos un destinatario'); return; }
    if (!subject.trim()) { toast.error('Asunto requerido'); return; }
    setSending(true);
    try {
      await API.post('/email/send', {
        account_id: accountId,
        to: toArr,
        subject,
        body_text: body,
        body_html: body.replace(/\n/g, '<br>'),
        in_reply_to_id: replyTo?.id,
        orden_id: ordenId,
      });
      toast.success('Correo enviado');
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Redactar correo</h2>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
      </div>
      <div className="space-y-3">
        {accounts.length > 1 && (
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="w-full rounded border px-3 py-2 text-sm"
          >
            {accounts.map(a => <option key={a.id} value={a.id}>{a.label} · {a.email}</option>)}
          </select>
        )}
        <Input
          placeholder="Para: (separar por coma)"
          value={toInput}
          onChange={(e) => setToInput(e.target.value)}
          data-testid="composer-to"
        />
        <Input
          placeholder="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid="composer-subject"
        />
        <Textarea
          placeholder="Escribe tu mensaje…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={12}
          data-testid="composer-body"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSend} disabled={sending || !accountId} data-testid="composer-send">
            {sending ? 'Enviando…' : 'Enviar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export { Composer };
